/**
 * KPI Scorecard DTOs (Issue #1389)
 *
 * Models the EOS-style weekly scorecard adapted to multi-team and multi-partner
 * structure. KPIs belong to one of three scopes: business-wide, a team, or a
 * partner. Measurements are weekly cells (Monday-anchored), recorded manually
 * or auto-pulled (Phase 2) from owning services.
 */
import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

export const ScorecardScopeTypeSchema = z.enum(['business', 'team', 'partner']);
export type ScorecardScopeType = z.infer<typeof ScorecardScopeTypeSchema>;

export const ScorecardUnitSchema = z.enum(['count', 'currency', 'percentage', 'duration_days', 'months', 'custom']);
export type ScorecardUnit = z.infer<typeof ScorecardUnitSchema>;

export const ScorecardComparatorSchema = z.enum(['gte', 'lte', 'eq', 'between']);
export type ScorecardComparator = z.infer<typeof ScorecardComparatorSchema>;

export const ScorecardSourceSchema = z.enum(['manual', 'computed']);
export type ScorecardSource = z.infer<typeof ScorecardSourceSchema>;

// ============================================================================
// ScorecardKpi
// ============================================================================

export const ScorecardKpiSchema = z
  .object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    scopeType: ScorecardScopeTypeSchema,
    /** null when scopeType=business; team id or partner id otherwise */
    scopeId: z.string().uuid().nullable(),
    name: z.string().min(1).max(255),
    ownerUserId: z.string().uuid(),
    unit: ScorecardUnitSchema,
    comparator: ScorecardComparatorSchema,
    goalValue: z.number().finite(),
    /** Required only when comparator=between */
    goalValueMax: z.number().finite().nullable(),
    source: ScorecardSourceSchema.default('manual'),
    /** When source=computed, opaque service-specific reference */
    computedQuery: z.string().nullable(),
    sortOrder: z.number().int().nonnegative().default(0),
    archivedAt: z.date().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .superRefine((value, ctx) => {
    if (value.scopeType === 'business' && value.scopeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeId'],
        message: 'scopeId must be null when scopeType is business',
      });
    }
    if (value.scopeType !== 'business' && !value.scopeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeId'],
        message: 'scopeId is required for team or partner scope',
      });
    }
    if (value.comparator === 'between' && value.goalValueMax === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['goalValueMax'],
        message: 'goalValueMax is required when comparator is between',
      });
    }
    if (value.comparator === 'between' && value.goalValueMax !== null && value.goalValueMax <= value.goalValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['goalValueMax'],
        message: 'goalValueMax must be greater than goalValue',
      });
    }
  });
export type ScorecardKpi = z.infer<typeof ScorecardKpiSchema>;

// ============================================================================
// Create / Update DTOs
// ============================================================================

export const CreateScorecardKpiSchema = z
  .object({
    scopeType: ScorecardScopeTypeSchema,
    scopeId: z.string().uuid().nullable().optional(),
    name: z.string().min(1).max(255),
    ownerUserId: z.string().uuid(),
    unit: ScorecardUnitSchema,
    comparator: ScorecardComparatorSchema,
    goalValue: z.number().finite(),
    goalValueMax: z.number().finite().nullable().optional(),
    source: ScorecardSourceSchema.optional(),
    computedQuery: z.string().nullable().optional(),
    sortOrder: z.number().int().nonnegative().optional(),
  })
  .superRefine((value, ctx) => {
    const scopeId = value.scopeId ?? null;
    if (value.scopeType === 'business' && scopeId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeId'],
        message: 'scopeId must be null when scopeType is business',
      });
    }
    if (value.scopeType !== 'business' && !scopeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scopeId'],
        message: 'scopeId is required for team or partner scope',
      });
    }
    if (value.comparator === 'between' && (value.goalValueMax === null || value.goalValueMax === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['goalValueMax'],
        message: 'goalValueMax is required when comparator is between',
      });
    }
    if (
      value.comparator === 'between' &&
      value.goalValueMax !== null &&
      value.goalValueMax !== undefined &&
      value.goalValueMax <= value.goalValue
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['goalValueMax'],
        message: 'goalValueMax must be greater than goalValue',
      });
    }
  });
export type CreateScorecardKpi = z.infer<typeof CreateScorecardKpiSchema>;

export const UpdateScorecardKpiSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  ownerUserId: z.string().uuid().optional(),
  unit: ScorecardUnitSchema.optional(),
  comparator: ScorecardComparatorSchema.optional(),
  goalValue: z.number().finite().optional(),
  goalValueMax: z.number().finite().nullable().optional(),
  source: ScorecardSourceSchema.optional(),
  computedQuery: z.string().nullable().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  archivedAt: z.date().nullable().optional(),
});
export type UpdateScorecardKpi = z.infer<typeof UpdateScorecardKpiSchema>;

export const DeleteScorecardKpiSchema = z.object({
  id: z.string().uuid(),
});
export type DeleteScorecardKpi = z.infer<typeof DeleteScorecardKpiSchema>;

// ============================================================================
// ScorecardMeasurement
// ============================================================================

export const ScorecardMeasurementSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  kpiId: z.string().uuid(),
  /** Monday of the ISO week the measurement covers (UTC midnight) */
  weekStart: z.date(),
  value: z.number().finite(),
  enteredByUserId: z.string().uuid(),
  enteredAt: z.date(),
  note: z.string().max(2000).nullable(),
});
export type ScorecardMeasurement = z.infer<typeof ScorecardMeasurementSchema>;

export const RecordScorecardMeasurementSchema = z.object({
  kpiId: z.string().uuid(),
  weekStart: z.coerce.date(),
  value: z.number().finite(),
  note: z.string().max(2000).nullable().optional(),
});
export type RecordScorecardMeasurement = z.infer<typeof RecordScorecardMeasurementSchema>;

// ============================================================================
// List filters
// ============================================================================

export const ListScorecardKpisInputSchema = z.object({
  scopeType: ScorecardScopeTypeSchema,
  scopeId: z.string().uuid().nullable().optional(),
  includeArchived: z.boolean().optional(),
});
export type ListScorecardKpisInput = z.infer<typeof ListScorecardKpisInputSchema>;

export const ListScorecardMeasurementsInputSchema = z.object({
  kpiId: z.string().uuid(),
  /** Number of weeks back from the current Monday (default 8, max 52) */
  weeks: z.number().int().min(1).max(52).optional(),
});
export type ListScorecardMeasurementsInput = z.infer<typeof ListScorecardMeasurementsInputSchema>;

// ============================================================================
// Scope discovery (drives the scope switcher)
// ============================================================================

export const ScorecardScopeSchema = z.object({
  scopeType: ScorecardScopeTypeSchema,
  /** null for business-wide */
  scopeId: z.string().uuid().nullable(),
  /** Display name (team name, partner name, or business name) */
  name: z.string(),
  /** For partners: name of the assigned partner manager (display only) */
  managerName: z.string().nullable().optional(),
  /** For partners: user id of the assigned partner manager */
  managerUserId: z.string().uuid().nullable().optional(),
});
export type ScorecardScope = z.infer<typeof ScorecardScopeSchema>;

export const ListScorecardScopesResponseSchema = z.object({
  business: ScorecardScopeSchema.nullable(),
  teams: z.array(ScorecardScopeSchema),
  partners: z.array(ScorecardScopeSchema),
});
export type ListScorecardScopesResponse = z.infer<typeof ListScorecardScopesResponseSchema>;

// ============================================================================
// Cell evaluation helpers (pure — used by both backend and UI)
// ============================================================================

export type CellState = 'green' | 'yellow' | 'red' | 'neutral';

/**
 * Evaluate a measurement value against a KPI goal.
 *
 * Returns 'neutral' when value is null/undefined, 'green' when the value hits
 * the goal per the comparator, 'yellow' when within ±10% of the goal but not
 * hitting it, 'red' otherwise.
 *
 * Why a shared helper: the same logic must run server-side (for computed KPIs
 * + audit signals) and client-side (cell color + sparkline accent), so we
 * keep one source of truth here and import it in both layers.
 */
export function evaluateScorecardCell(
  value: number | null | undefined,
  comparator: ScorecardComparator,
  goal: number,
  goalMax: number | null = null,
): CellState {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'neutral';
  }
  const tolerance = Math.abs(goal) * 0.1;
  switch (comparator) {
    case 'gte': {
      if (value >= goal) return 'green';
      if (value >= goal - tolerance) return 'yellow';
      return 'red';
    }
    case 'lte': {
      if (value <= goal) return 'green';
      if (value <= goal + tolerance) return 'yellow';
      return 'red';
    }
    case 'eq': {
      if (value === goal) return 'green';
      if (Math.abs(value - goal) <= tolerance) return 'yellow';
      return 'red';
    }
    case 'between': {
      const upper = goalMax ?? goal;
      if (value >= goal && value <= upper) return 'green';
      const upperTolerance = Math.abs(upper) * 0.1;
      if (value >= goal - tolerance && value <= upper + upperTolerance) return 'yellow';
      return 'red';
    }
    default:
      return 'neutral';
  }
}

/**
 * Returns the Monday (UTC midnight) of the ISO week containing the given date.
 * Used to anchor weekStart values consistently across server and client.
 */
export function isoWeekMonday(date: Date = new Date()): Date {
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // 0=Sun..6=Sat → days back to Monday
  const day = utc.getUTCDay();
  const offset = day === 0 ? 6 : day - 1;
  utc.setUTCDate(utc.getUTCDate() - offset);
  return utc;
}
